import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from 'react-query';
import { Toaster } from 'react-hot-toast';
import { AuthProvider } from './contexts/AuthContext';
import ProtectedRoute from './components/ProtectedRoute';
import Layout from './components/Layout/Layout';
import Login from './pages/Login';
import SystemLogin from './pages/SystemLogin';
import ForgotPassword from './pages/ForgotPassword';
import Dashboard from './pages/Dashboard';
import Employees from './pages/Employees';
import Attendance from './pages/Attendance';
import MyAttendance from './pages/MyAttendance';
import Reports from './pages/Reports';
import Settings from './pages/Settings';
import Profile from './pages/Profile';
import JiraTimeTracking from './pages/JiraTimeTracking';
import LeaveApplication from './pages/LeaveApplication';
import LeaveApproval from './pages/LeaveApproval';
import LeaveCalendar from './pages/LeaveCalendar';
import YearlyLeaveSettings from './pages/YearlyLeaveSettings';
import LeaveTypes from './pages/LeaveTypes';
import HolidayManager from './components/HolidayManager';
import SystemTenants from './pages/SystemTenants';
import QrAttendance from './pages/QrAttendance';
import ScanQr from './pages/ScanQr';
import './index.css';

// Create a client
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 1,
      refetchOnWindowFocus: false,
    },
  },
});

function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <AuthProvider>
        <Router
          future={{
            v7_startTransition: true,
            v7_relativeSplatPath: true
          }}
        >
          <div className="App">
            <Routes>
              <Route path="/login" element={<Login />} />
              <Route path="/system-login" element={<SystemLogin />} />
              <Route path="/forgot-password" element={<ForgotPassword />} />
              <Route
                path="/system/tenants"
                element={
                  <ProtectedRoute requiredRole="system">
                    <Layout>
                      <SystemTenants />
                    </Layout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/dashboard"
                element={
                  <ProtectedRoute>
                    <Layout>
                      <Dashboard />
                    </Layout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/employees"
                element={
                  <ProtectedRoute requiredRole="admin">
                    <Layout>
                      <Employees />
                    </Layout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/attendance"
                element={
                  <ProtectedRoute requiredRole="admin">
                    <Layout>
                      <Attendance />
                    </Layout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/qr-attendance"
                element={
                  <ProtectedRoute requiredRole="admin">
                    <Layout>
                      <QrAttendance />
                    </Layout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/scan-qr"
                element={
                  <ProtectedRoute>
                    <Layout>
                      <ScanQr />
                    </Layout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/jira-time-tracking"
                element={
                  <ProtectedRoute requiredRole="admin">
                    <Layout>
                      <JiraTimeTracking />
                    </Layout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/reports"
                element={
                  <ProtectedRoute requiredRole="admin">
                    <Layout>
                      <Reports />
                    </Layout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/holidays"
                element={
                  <ProtectedRoute requiredRole="admin">
                    <Layout>
                      <HolidayManager />
                    </Layout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/settings"
                element={
                  <ProtectedRoute requiredRole="admin">
                    <Layout>
                      <Settings />
                    </Layout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/profile"
                element={
                  <ProtectedRoute>
                    <Layout>
                      <Profile />
                    </Layout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/leave-application"
                element={
                  <ProtectedRoute>
                    <Layout>
                      <LeaveApplication />
                    </Layout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/leave-approval"
                element={
                  <ProtectedRoute requiredRole="admin">
                    <Layout>
                      <LeaveApproval />
                    </Layout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/leave-calendar"
                element={
                  <ProtectedRoute>
                    <Layout>
                      <LeaveCalendar />
                    </Layout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/leave-types"
                element={
                  <ProtectedRoute requiredRole="admin">
                    <Layout>
                      <LeaveTypes />
                    </Layout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/yearly-leave-settings"
                element={
                  <ProtectedRoute requiredRole="admin">
                    <Layout>
                      <YearlyLeaveSettings />
                    </Layout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/my-attendance"
                element={
                  <ProtectedRoute>
                    <Layout>
                      <MyAttendance />
                    </Layout>
                  </ProtectedRoute>
                }
              />
              <Route path="/" element={<Navigate to="/dashboard" replace />} />
              <Route path="*" element={<Navigate to="/dashboard" replace />} />
            </Routes>
            
            <Toaster
              position="top-right"
              toastOptions={{
                duration: 4000,
                style: {
                  background: '#363636',
                  color: '#fff',
                },
                success: {
                  duration: 3000,
                  theme: {
                    primary: '#4aed88',
                  },
                },
              }}
            />
          </div>
        </Router>
      </AuthProvider>
    </QueryClientProvider>
  );
}

export default App;
